import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Phone, Calendar, Edit2, Trash2, RefreshCw, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SortHeader } from '../../components/SortHeader';
import { useSortableData } from '../../hooks/useSortableData';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  'New Lead': 'bg-blue-100 text-blue-700',
  'Appointment Confirmed': 'bg-green-100 text-green-700',
  'Missed Appointment': 'bg-red-100 text-red-700',
  'Closed': 'bg-gray-100 text-gray-600',
  'Follow-up': 'bg-yellow-100 text-yellow-700',
};

const STATUSES = ['New Lead', 'Appointment Confirmed', 'Missed Appointment', 'Follow-up', 'Closed'];
const APPT_FOR = ['Specialities', 'Scan', 'Lab Test', 'General Checkup', 'Emergency', 'Pharmacy'];
const DOCTORS = ['DR.Priyakanth', 'DR.Swamyram', 'DR.Janani', 'DR.Gowtham', 'DR.Suresh Krishna', 'DR.Swetha', 'DR.Desanesan', 'DR.Faruk Mohammad'];

const emptyForm = {
  enquiry_date: new Date().toISOString().split('T')[0],
  patient_name: '',
  mobile_no: '',
  appointment_date: new Date().toISOString().split('T')[0],
  appointment_for: '',
  doctor_specialist: '',
  lead_status: 'New Lead',
  notes: '',
  next_followup_date: '',
  action_required: '',
};

const AppointmentModule = ({ user }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff/appointments`, { withCredentials: true });
      setAppointments(data);
    } catch { toast.error('Failed to load appointments'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/staff/appointments/${editId}`, form, { withCredentials: true });
        toast.success('Appointment updated');
      } else {
        await axios.post(`${API}/staff/appointments`, form, { withCredentials: true });
        toast.success('Appointment created');
      }
      setForm(emptyForm);
      setEditId(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (appt) => {
    setForm({
      enquiry_date: appt.enquiry_date || '',
      patient_name: appt.patient_name || '',
      mobile_no: appt.mobile_no || '',
      appointment_date: appt.appointment_date || '',
      appointment_for: appt.appointment_for || '',
      doctor_specialist: appt.doctor_specialist || '',
      lead_status: appt.lead_status || 'New Lead',
      notes: appt.notes || '',
      next_followup_date: appt.next_followup_date || '',
      action_required: appt.action_required || '',
    });
    setEditId(appt.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await axios.delete(`${API}/staff/appointments/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const handleStatusChange = async (appt, newStatus) => {
    try {
      await axios.put(`${API}/staff/appointments/${appt.id}`, { ...appt, lead_status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const filtered = appointments.filter(a => {
    const matchSearch = !search || a.patient_name?.toLowerCase().includes(search.toLowerCase()) || a.mobile_no?.includes(search);
    const matchStatus = filterStatus === 'All' || a.lead_status === filterStatus;
    return matchSearch && matchStatus;
  });

  const { sorted, sortKey, sortDir, requestSort } = useSortableData(filtered, 'appointment_date', 'desc');

  const statusCounts = {};
  STATUSES.forEach(s => { statusCounts[s] = appointments.filter(a => a.lead_status === s).length; });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {STATUSES.map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(filterStatus === status ? 'All' : status)}
            className={`p-3 rounded-xl border-2 transition-all text-left ${filterStatus === status ? 'border-[#0A2540] shadow-md' : 'border-transparent bg-white shadow-sm'}`}
            data-testid={`status-filter-${status.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-1 ${STATUS_COLORS[status]}`}>{status}</div>
            <div className="text-2xl font-bold text-[#0A2540]">{statusCounts[status] || 0}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
          <Input placeholder="Search by name or phone..." className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="appt-search" />
        </div>
        <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
        <Button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }} className="bg-[#0A2540]" data-testid="add-appointment-btn">
          <Plus className="h-4 w-4 mr-2" /> New Appointment
        </Button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A2540]">{editId ? 'Edit' : 'New'} Appointment</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Enquiry Date</Label><Input type="date" value={form.enquiry_date} onChange={e => setForm({ ...form, enquiry_date: e.target.value })} required data-testid="appt-enquiry-date" /></div>
                <div><Label>Appointment Date</Label><Input type="date" value={form.appointment_date} onChange={e => setForm({ ...form, appointment_date: e.target.value })} required data-testid="appt-date" /></div>
              </div>
              <div><Label>Patient Name</Label><Input value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} required data-testid="appt-patient-name" /></div>
              <div><Label>Mobile No</Label><Input value={form.mobile_no} onChange={e => setForm({ ...form, mobile_no: e.target.value })} required data-testid="appt-mobile" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Appointment For</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.appointment_for} onChange={e => setForm({ ...form, appointment_for: e.target.value })} data-testid="appt-for">
                    <option value="">Select...</option>
                    {APPT_FOR.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Doctor/Specialist</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.doctor_specialist} onChange={e => setForm({ ...form, doctor_specialist: e.target.value })} data-testid="appt-doctor">
                    <option value="">Select...</option>
                    {DOCTORS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Lead Status</Label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.lead_status} onChange={e => setForm({ ...form, lead_status: e.target.value })} data-testid="appt-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="appt-notes" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Next Follow-up</Label><Input type="date" value={form.next_followup_date} onChange={e => setForm({ ...form, next_followup_date: e.target.value })} data-testid="appt-followup" /></div>
                <div><Label>Action Required</Label><Input value={form.action_required} onChange={e => setForm({ ...form, action_required: e.target.value })} data-testid="appt-action" /></div>
              </div>
              <Button type="submit" className="w-full bg-[#0A2540]" data-testid="appt-submit">{editId ? 'Update' : 'Create'} Appointment</Button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9]">
              <tr>
                <SortHeader label="Patient" sortKey="patient_name" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Phone" sortKey="mobile_no" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} className="hidden sm:table-cell" />
                <SortHeader label="For" sortKey="appointment_for" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Doctor" sortKey="doctor_specialist" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} className="hidden md:table-cell" />
                <SortHeader label="Date" sortKey="appointment_date" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Status" sortKey="lead_status" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <th className="px-3 py-3 text-left font-semibold text-[#0A2540] hidden lg:table-cell">Notes</th>
                <th className="px-3 py-3 text-right font-semibold text-[#0A2540]">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#94A3B8]">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#94A3B8]">No appointments found</td></tr>
              ) : sorted.map(appt => (
                <tr key={appt.id} className="hover:bg-[#F8FAFC]" data-testid={`appt-row-${appt.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-[#0A2540]">{appt.patient_name}</div>
                    <div className="text-xs text-[#94A3B8]">{appt.enquiry_date}</div>
                  </td>
                  <td className="px-3 py-3 hidden sm:table-cell">
                    <a href={`tel:${appt.mobile_no}`} className="text-[#0EA5E9] hover:underline flex items-center">
                      <Phone className="h-3 w-3 mr-1" />{appt.mobile_no}
                    </a>
                  </td>
                  <td className="px-3 py-3">{appt.appointment_for}</td>
                  <td className="px-3 py-3 hidden md:table-cell">{appt.doctor_specialist}</td>
                  <td className="px-3 py-3 text-[#475569]">{appt.appointment_date}</td>
                  <td className="px-3 py-3">
                    <select
                      className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${STATUS_COLORS[appt.lead_status] || 'bg-gray-100'}`}
                      value={appt.lead_status}
                      onChange={(e) => handleStatusChange(appt, e.target.value)}
                      data-testid={`appt-status-select-${appt.id}`}
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell max-w-[200px] truncate text-[#475569]">{appt.notes}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <button onClick={() => handleEdit(appt)} className="p-1.5 rounded-lg hover:bg-[#F1F5F9]" data-testid={`edit-appt-${appt.id}`}>
                        <Edit2 className="h-4 w-4 text-[#475569]" />
                      </button>
                      {['admin', 'receptionist'].includes(user.role) && (
                        <button onClick={() => handleDelete(appt.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-appt-${appt.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AppointmentModule;
